import type {
  PhotoCrewMember,
  PhotoGroup,
  PhotoPlan,
  PhotoRole,
  ScheduleEvent,
  Supplier,
  WeddingData,
} from './types'

export const photoRoleLabels: Record<PhotoRole, string> = {
  photo: 'Fotograf',
  video: 'Kameraman',
  second: 'Druhý fotograf',
  booth: 'Fotokoutek',
  content: 'Tvůrce obsahu',
}

/** Supplier categories offered when picking someone for a role. */
export const photoRoleCategories: Record<PhotoRole, string[]> = {
  photo: ['photographer'],
  video: ['videographer'],
  second: ['photographer'],
  booth: ['photographer', 'entertainment'],
  content: ['videographer', 'photographer'],
}

export function emptyPhoto(): PhotoPlan {
  return {
    crew: [newCrewMember('crew-photo', 'photo'), newCrewMember('crew-video', 'video')],
    groups: [],
    caller: null,
    wishes: '',
  }
}

export function planningPhoto(): PhotoPlan {
  return {
    crew: [
      {
        id: 'crew-foto',
        role: 'photo',
        supplierId: 'sup-foto',
        arrival: '10:30',
        end: '19:30',
        note: 'Balíček Celý den. Jídlo pro fotografa domluvené s cateringem.',
        documents: [],
      },
      newCrewMember('crew-video', 'video'),
    ],
    groups: [
      group('grp-bride-parents', 'Novomanželé s rodiči nevěsty', ['g1', 'g2']),
      group('grp-grandma', 'Novomanželé s babičkou Marií', ['g12']),
      group('grp-groom-family', 'Novomanželé s rodinou ženicha', ['g3', 'g4', 'g5', 'g6']),
      group('grp-witnesses', 'Novomanželé se svědky', ['g15', 'g7']),
      { ...group('grp-all', 'Všichni hosté', []), everyone: true },
      group('grp-groom-friends', 'Kamarádi ženicha', ['g17', 'g21', 'g22', 'g26'], 'if-time'),
      group('grp-colleagues', 'Kolegyně z práce', ['g23', 'g24', 'g25'], 'if-time'),
      group('grp-urban', 'Novomanželé s Urbanovými', ['g27', 'g28'], 'if-time'),
    ],
    caller: { type: 'guest', id: 'g7' },
    wishes:
      'Hlavně přirozené momenty, aranžovaných fotek co nejmíň. Babičku Jarmilu prosíme vyfotit hned po obřadu, večer už nezůstane.',
  }
}

function group(
  id: string,
  name: string,
  guestIds: string[],
  priority: PhotoGroup['priority'] = 'must',
): PhotoGroup {
  return { id, name, everyone: false, guestIds, priority }
}

/** Tips for the group list; one disappears once a group has its name. */
export const groupTips = [
  'Všichni hosté',
  'Novomanželé s rodiči nevěsty',
  'Novomanželé s rodiči ženicha',
  'Novomanželé s prarodiči',
  'Novomanželé se svědky',
  'Novomanželé s družičkami',
  'Novomanželé s kamarády',
  'Novomanželé s kolegy',
]

/** A rough guess for planning: a big group takes longer to gather. */
export function groupMinutes(group: PhotoGroup) {
  return group.everyone ? 5 : 3
}

export function newCrewMember(id: string, role: PhotoRole): PhotoCrewMember {
  return { id, role, supplierId: null, arrival: null, end: null, note: '', documents: [] }
}

export type CrewWithSupplier = PhotoCrewMember & { supplier: Supplier | null }

export function photoOverview(data: WeddingData) {
  return {
    crew: data.photo.crew.map((member) => ({
      ...member,
      supplier: data.suppliers.find((item) => item.id === member.supplierId) ?? null,
    })),
    groups: data.photo.groups.length
      ? {
          count: data.photo.groups.length,
          must: data.photo.groups.filter((item) => item.priority === 'must').length,
          minutes: data.photo.groups.reduce((sum, item) => sum + groupMinutes(item), 0),
        }
      : null,
  }
}

/** Minutes from midnight; times before 06:00 belong to the night after the wedding day. */
export function dayMinutes(time: string) {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  return (hours < 6 ? hours + 24 : hours) * 60 + minutes
}

const arrivalTitle = (role: PhotoRole, supplier: Supplier) =>
  `Příjezd: ${photoRoleLabels[role]} · ${supplier.name}`

/**
 * Saves a crew member and keeps their arrival in Harmonogram: one "Příjezd" event per supplier,
 * moved with the arrival time and removed when the arrival or the supplier goes.
 */
export function saveCrewMember(
  data: WeddingData,
  member: PhotoCrewMember,
  newEventId: string,
): WeddingData {
  const previous = data.photo.crew.find((item) => item.id === member.id)
  const crew = previous
    ? data.photo.crew.map((item) => (item.id === member.id ? member : item))
    : [...data.photo.crew, member]
  const isArrival = (event: ScheduleEvent, supplierId: string | null) =>
    event.source?.section === 'photo' &&
    event.owner?.type === 'supplier' &&
    event.owner.id === supplierId &&
    event.title.startsWith('Příjezd:')
  let schedule = data.schedule.filter(
    (event) =>
      !previous ||
      previous.supplierId === member.supplierId ||
      !isArrival(event, previous.supplierId),
  )
  const supplier = data.suppliers.find((item) => item.id === member.supplierId)
  if (supplier) {
    const existing = schedule.find((event) => isArrival(event, supplier.id))
    if (!member.arrival) schedule = schedule.filter((event) => event !== existing)
    else if (existing)
      schedule = schedule.map((event) =>
        event === existing
          ? { ...event, time: member.arrival, title: arrivalTitle(member.role, supplier) }
          : event,
      )
    else
      schedule = [
        ...schedule,
        {
          id: newEventId,
          time: member.arrival,
          endTime: null,
          title: arrivalTitle(member.role, supplier),
          description: '',
          location: '',
          audience: 'team',
          dayPartId: null,
          owner: { type: 'supplier', id: supplier.id },
          source: { section: 'photo', label: 'Foto a video' },
          removed: false,
        },
      ]
  }
  return { ...data, photo: { ...data.photo, crew }, schedule }
}

export function removeCrewMember(data: WeddingData, id: string): WeddingData {
  const member = data.photo.crew.find((item) => item.id === id)
  return {
    ...data,
    photo: { ...data.photo, crew: data.photo.crew.filter((item) => item.id !== id) },
    schedule: data.schedule.filter(
      (event) =>
        !(
          event.source?.section === 'photo' &&
          event.title.startsWith('Příjezd:') &&
          event.owner?.id === member?.supplierId
        ),
    ),
  }
}
